import { useState, useEffect, useCallback } from 'react';
import UpdateCategoryForm from "../../components/update/update-categories-form";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading'; 

export default function Page() {
  const { token } = useAuth();
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategory = useCallback(async () => {
    if (!id) {
      setError("L'ID de la catégorie est manquant."); 
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);   
    try {
      if (!token) {
        toast.error("Vous n'êtes pas authentifié. Veuillez vous connecter."); // French: You are not authenticated. Please log in.
        setError("Jeton d'authentification introuvable. Veuillez vous connecter."); // French: Authentication token not found. Please log in.
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/categories/${id}`, { 
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, 
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `Échec de la récupération de la catégorie : ${res.statusText}`; 
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.status && data.data) {
        setCategory(data.data); // Set the fetched category data
      } else {
        throw new Error(data.message || "Format de données invalide de l'API."); // French: Invalid data format from API.
      }
    } catch (err) {
      console.error("Erreur lors de la récupération de la catégorie :", err); // French: Error fetching category
      setError(err.message || "Une erreur inattendue est survenue lors de la récupération de la catégorie."); // French: An unexpected error occurred while fetching the category.
      toast.error(`Erreur : ${err.message || "Échec du chargement de la catégorie."}`); // French: Error: Failed to load category.
    } finally {
      setLoading(false); // End loading regardless of success or failure
    }
  }, [id]); // Dependency on 'id' ensures re-fetch if ID changes

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Erreur lors du chargement de la catégorie</h2> {/* French: Error Loading Category */}
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchCategory} 
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            Réessayer 
          </button>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Catégorie Introuvable</h2> {/* French: Category Not Found */}
          <p className="text-gray-600">La catégorie avec l'ID "{id}" n'a pas pu être chargée ou n'existe pas.</p> {/* French: The category with ID "{id}" could not be loaded or does not exist. */}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            Modifier la Catégorie
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            Modifiez les détails de cette catégorie spécifique.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <UpdateCategoryForm category={category} />
        </div>
      </div>
    </div>
  );
}
